import { FC, useEffect, useState } from 'react'
import { IUserGoal } from './utils/types'
import {
  createUserGoal,
  getUserGoals,
  removeUserGoal,
  updateUserGoal,
} from './utils/api'
import GoalCard from './components/GoalCard/GoalCard'
import { Empty } from 'antd'
import Preloader from '@UI/Preloader'
import AddGoalCard from './components/AddGoalCard/AddGoalCard'
import jclasses from '@pages/Admin/CRM/styles/jsmodal.module.css'
import { Modal as AModal } from 'antd'
import { useAddAlert } from '@/hooks/useAddAlert'
import TextAreaField from '@pages/Admin/CourseSettings/components/TextAreaField/TextAreaField'
import cn from './index.module.css'

interface IGoalsList {
  userId: number
}

function GoalModalContent({
  text,
  onChange,
}: {
  text: string
  onChange: (value: string) => void
}) {
  const [newText, setNewText] = useState(text)

  return (
    <TextAreaField
      className={cn.textarea}
      placeholder="Моя цель"
      value={newText}
      onChange={(value) => {
        setNewText(value?.target?.value)
        onChange(value?.target?.value)
      }}
      autoSize={{ minRows: 3, maxRows: 6 }}
    />
  )
}

const GoalsList: FC<IGoalsList> = ({ userId }) => {
  const addAlert = useAddAlert()
  const [loading, setLoading] = useState<boolean>(true)
  const [goals, setGoals] = useState<IUserGoal[]>([])

  const fetchGoals = async () => {
    setLoading(true)
    const goals = await getUserGoals(userId)
    setGoals(goals)
    setLoading(false)
  }

  useEffect(() => {
    fetchGoals()
  }, [userId])

  const onRemove = async (goal: IUserGoal) => {
    const isApproved = await new Promise((resolve) => {
      AModal.confirm({
        title: 'Удалить',
        content: 'Вы уверены, что хотите удалить эту цель?',
        icon: null,
        centered: true,
        maskClosable: true,
        className: jclasses.modal,
        okText: 'Подтвердить',
        cancelText: 'Отменить',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })

    if (isApproved) {
      const res = await removeUserGoal(goal.id)

      if (res) {
        addAlert({ title: 'Успешно', text: 'Цель удалена из списка' })
        setGoals((prev) => prev.filter((item) => item.id !== goal.id))
      } else {
        addAlert({
          title: 'Ошибка',
          text: 'Попробуйте ещё раз или обратитесь к администратору',
          type: 'error',
        })
      }
    }
  }

  const onUpdateGoal = async (goal?: IUserGoal) => {
    const modalRes: { resolve: boolean; value?: string } = await new Promise(
      (resolve) => {
        let text = goal?.title ?? ''

        AModal.confirm({
          title: goal?.id ? 'Обновить цель' : 'Создать цель',
          content: (
            <GoalModalContent
              text={text}
              onChange={(value) => {
                text = value
              }}
            />
          ),
          icon: null,
          centered: true,
          maskClosable: true,
          className: jclasses.modal,
          okText: 'Сохранить',
          cancelText: 'Отменить',
          onOk: () => resolve({ resolve: true, value: text }),
          onCancel: () => resolve({ resolve: false }),
        })
      },
    )

    if (modalRes?.resolve && modalRes.value) {
      if (goal?.id) {
        const res = await updateUserGoal({ id: goal.id, title: modalRes.value })

        if (!!res) {
          addAlert({ title: 'Успешно', text: 'Цель обновлена' })
          setGoals((prev) =>
            prev.map((item) => {
              if (item.id === goal?.id) {
                return res
              } else {
                return item
              }
            }),
          )
        } else {
          addAlert({
            title: 'Ошибка',
            text: 'Попробуйте ещё раз или обратитесь к администратору',
            type: 'error',
          })
        }
      } else {
        const res = await createUserGoal(userId, {
          id: 0,
          title: modalRes.value,
        })

        if (!!res) {
          addAlert({ title: 'Успешно', text: 'Цель сохранена' })
          setGoals((prev) => [...prev, res])
        } else {
          addAlert({
            title: 'Ошибка',
            text: 'Попробуйте ещё раз или обратитесь к администратору',
            type: 'error',
          })
        }
      }
    }
  }

  return (
    <div className={cn.wrapper}>
      {loading ? (
        <div className={cn.stubWrapper}>
          <Preloader />
        </div>
      ) : !goals?.length ? (
        <div className={cn.stubWrapper}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={'Добавьте новую цель'}
          />
          <AddGoalCard onClick={onUpdateGoal} />
        </div>
      ) : (
        <div className={cn.list}>
          {goals.map((item) => {
            return (
              <GoalCard
                key={item?.id}
                goal={item}
                onClick={() => onUpdateGoal(item)}
                onRemove={() => onRemove(item)}
              />
            )
          })}
          <AddGoalCard onClick={onUpdateGoal} />
        </div>
      )}
    </div>
  )
}

export default GoalsList
